'use client'

import { useState, useEffect, useCallback } from 'react'
import { GraduationCap, Zap, Target } from 'lucide-react'
import { TEACHING_MODES, getTeachingMode, setTeachingMode, subscribeTeachingMode } from '@/lib/adminTeachingMode'

// Teaching-source: lib/adminTeachingMode.js — TEACHING_MODES = ['novice', 'expert', 'focus']

const MODE_META = {
  novice: {
    label: '新手模式',
    shortLabel: '新手',
    description: '顯示所有教學提示、導覽與說明卡',
    Icon: GraduationCap,
    next: 'expert',
  },
  expert: {
    label: '熟手模式',
    shortLabel: '熟手',
    description: '教學卡收起，保留手動查閱提示',
    Icon: Zap,
    next: 'focus',
  },
  focus: {
    label: '專注模式',
    shortLabel: '專注',
    description: '隱藏所有教學提示，只保留操作安全網',
    Icon: Target,
    next: 'novice',
  },
}

/**
 * Three-state pill toggle for admin teaching mode.
 * Cycles: novice → expert → focus → novice on each click.
 * Persists to localStorage via lib/adminTeachingMode.
 * Adds data-tour="teaching-mode" for SpotlightTour targeting.
 *
 * Teaching source: lib/adminTeachingMode.js
 */
export default function TeachingModeToggle() {
  const [mode, setMode] = useState(getTeachingMode)

  // Keep in sync if another tab / component changes the mode
  useEffect(() => {
    const unsub = subscribeTeachingMode((newMode) => setMode(newMode))
    return unsub
  }, [])

  const handleClick = useCallback(() => {
    const next = MODE_META[mode].next
    setTeachingMode(next)
    // setMode handled by subscription above — avoids double render
  }, [mode])

  const { label, shortLabel, description, Icon } = MODE_META[mode]

  return (
    <button
      type="button"
      onClick={handleClick}
      data-tour="teaching-mode"
      data-teaching-mode={mode}
      aria-label={`教學模式：${label}（點擊切換）`}
      title={description}
      className="admin-teaching-toggle"
    >
      <Icon size={15} strokeWidth={2} aria-hidden="true" className="admin-teaching-toggle__icon" />
      <span className="admin-teaching-toggle__label">{shortLabel}</span>
    </button>
  )
}
